'use client'

import React from 'react'

import { useExtractSpaceProps } from '../../hooks'
import { SpaceProps } from '../../themes'
import { Field } from '../Field'
import { SelectInput, SelectInputProps } from './SelectInput'

export type { Option } from './SelectInput'

export interface SelectFieldProps<T extends string | number | null | undefined>
  extends Omit<SelectInputProps<T>, 'hasError'>,
    SpaceProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Field label
   */
  label?: string

  /**
   * Required flag
   */
  required?: boolean

  /**
   * Icon to display before the error message
   */
  errorIcon?: React.ReactNode

  /**
   * Error messages
   */
  errorMessages?: string | string[]

  /**
   * Icon to display before the caption message
   */
  captionIcon?: React.ReactNode

  /**
   * Caption
   */
  captionMessages?: string | string[]

  /**
   * Disable body scroll lock
   */
  disableBodyScrollLock?: boolean
}

const SelectFieldInner = <T extends string | number | undefined>(
  {
    id,
    className,
    label,
    required,
    autoWidth,
    placement = 'bottom',
    errorIcon,
    errorMessages,
    captionIcon,
    captionMessages,
    ...props
  }: SelectFieldProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const autoId = React.useId()
  const inputId = id ?? autoId
  const [spaceProps, selectProps] = useExtractSpaceProps(props)

  return (
    <Field className={className} {...spaceProps}>
      {label && (
        <Field.Label id={inputId} required={required}>
          {label}
        </Field.Label>
      )}

      <Field.Content>
        <SelectInput<T>
          id={inputId}
          hasError={!!errorMessages}
          ref={ref}
          autoWidth={autoWidth}
          placement={placement}
          {...selectProps}
        />
      </Field.Content>

      <Field.Messages
        mt={2}
        type="error"
        messages={errorMessages}
        icon={errorIcon}
      />

      <Field.Messages
        mt={errorMessages || (errorMessages && errorMessages.length) ? 1 : 2}
        type="caption"
        messages={captionMessages}
        icon={captionIcon}
      />
    </Field>
  )
}

export const SelectField = React.forwardRef(SelectFieldInner) as <
  T extends string | number | null | undefined,
>(
  props: SelectFieldProps<T> & { ref?: React.ForwardedRef<HTMLLIElement> },
) => ReturnType<typeof SelectFieldInner>
